// ИИ → Отчёты: недельные отчёты (по понедельникам) и прошлые советы на день — списком с датами.

import { useState } from 'react'

import { Badge, Button, Card, EmptyState, ErrorText, PageSpinner, Tabs, useToast } from '@/components/ui'
import { errorMessage } from '@/api/client'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/format'

import { useAiStatus, useGenerateWeekly, useReports, type Report } from './api'
import { reportPeriod } from './logic'
import { Disclaimer, Markdown, NotConfigured } from './parts'

type Kind = Report['kind']

const TABS = [
  { value: 'weekly', label: 'Недельные' },
  { value: 'daily_advice', label: 'Советы на день' },
] as const

function ReportBody({ report }: { report: Report }) {
  if (report.status === 'error') return <ErrorText>Отчёт не получился: {report.error}</ErrorText>
  return (
    <div className="grid gap-3">
      <Markdown text={report.content_md ?? ''} />
      <p className="text-xs text-muted">
        Сформирован {formatDateTime(report.updated_at)}
        {report.model ? ` · ${report.model}` : ''}
      </p>
      <Disclaimer />
    </div>
  )
}

export function ReportsPage() {
  const status = useAiStatus()
  const [kind, setKind] = useState<Kind>('weekly')
  const reports = useReports(kind)
  const generate = useGenerateWeekly()
  const toast = useToast()
  const [selected, setSelected] = useState<string | null>(null)

  const configured = status.data?.configured === true
  const items = reports.data?.items ?? []
  const current = items.find((r) => r.id === selected) ?? items[0] ?? null

  const makeWeekly = () =>
    generate.mutate(undefined, {
      onSuccess: (r) => {
        setKind('weekly')
        setSelected(r.id)
        toast.show({ message: 'Отчёт за прошлую неделю готов', tone: 'accent' })
      },
      onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
    })

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          items={TABS}
          value={kind}
          onChange={(v) => {
            setKind(v)
            setSelected(null)
          }}
          label="Вид отчёта"
        />
        {configured ? (
          <Button icon="refresh" loading={generate.isPending} onClick={makeWeekly}>
            Отчёт за прошлую неделю
          </Button>
        ) : null}
      </div>
      {status.data && !configured ? (
        <Card>
          <NotConfigured />
        </Card>
      ) : null}
      {generate.isError ? <ErrorText error={generate.error} /> : null}

      {reports.isPending ? (
        <PageSpinner />
      ) : reports.isError ? (
        <ErrorText error={reports.error} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon="history"
            title={kind === 'weekly' ? 'Недельных отчётов пока нет' : 'Советов пока нет'}
            description={
              kind === 'weekly'
                ? 'Отчёт приходит по понедельникам за прошлую неделю — день и время в настройках ИИ.'
                : 'Совет на сегодня появляется утром на экране «Сегодня».'
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <Card flush title="По датам">
            <ul className="pb-2">
              {items.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm tabular-nums',
                      current?.id === r.id ? 'font-medium text-accent' : 'text-fg hover:bg-surface-2',
                    )}
                  >
                    <span>{reportPeriod(r)}</span>
                    {r.status === 'error' ? <Badge tone="danger">сбой</Badge> : null}
                  </button>
                </li>
              ))}
            </ul>
          </Card>
          {current ? (
            <Card title={kind === 'weekly' ? `Неделя ${reportPeriod(current)}` : `Совет на ${reportPeriod(current)}`}>
              <ReportBody report={current} />
            </Card>
          ) : null}
        </div>
      )}
    </div>
  )
}
